/**
 * ClairePitchModal (Iter344 · B5) — inline preview & send.
 *
 * Opens over any leads surface with a Claire-generated cold email
 * (subject + body). The user can edit inline, send via Resend, or
 * save the current draft as a sequence template.
 *
 * Props:
 *   open, onClose
 *   leads          — array of `{id, name, email, title, company, city, state, segment, provider}`
 *                    (single-item = one-off; multi-item = batch send)
 *   authHeader     — passed through to axios
 *   onSent         — callback after successful send (single or batch)
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  X, Send, Loader2, RefreshCw, Sparkles, Save, Pencil, Mail,
  CheckCircle2, AlertTriangle, ExternalLink,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ClairePitchModal({ open, onClose, leads = [], authHeader, onSent }) {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);
  const [savingSeq, setSavingSeq] = useState(false);
  const [progress, setProgress] = useState(null); // { total, done, ok, fail }
  const [tone, setTone] = useState("consultative");
  const [language, setLanguage] = useState("pt");
  // Iter347 · Bypass: when the backend send-direct fails (Resend outage,
  // Cloudflare 502, missing key, etc.), surface a mailto link so the
  // vendor still has a working send path with subject/body pre-filled.
  const [lastError, setLastError] = useState(null);

  const isBatch = leads.length > 1;
  const seedLead = leads[0] || null;
  const missingEmail = leads.filter((L) => !(L.email || L.contact_email || L.decision_maker_email)).length;

  // Reset + auto-generate whenever we open with a new set of leads.
  useEffect(() => {
    if (!open || !seedLead) return;
    setSubject("");
    setBody("");
    setProgress(null);
    setLastError(null);
    void doGenerate(seedLead, tone, language);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, seedLead?.id]);

  const doGenerate = async (lead, curTone, curLang) => {
    setGenerating(true);
    try {
      const payload = {
        name:     lead.decision_maker_name  || lead.name         || "",
        title:    lead.decision_maker_title || lead.title        || "",
        email:    lead.decision_maker_email || lead.email        || "",
        company:  lead.company_name         || lead.property_name || lead.company || "",
        city:     lead.city                 || "",
        state:    lead.state                || "",
        segment:  lead.segment              || "",
        provider: lead.current_provider     || "",
        tone: curTone,
        language: curLang,
      };
      const r = await axios.post(`${API}/smith/outreach/generate-letter`, payload,
        { ...authHeader, timeout: 60000 });
      setSubject(r.data.subject || "");
      setBody(r.data.body || "");
    } catch (e) {
      toast.error("Claire não conseguiu redigir agora",
        { description: e?.response?.data?.detail || e.message });
    } finally { setGenerating(false); }
  };

  const doSend = async () => {
    if (!subject.trim() || !body.trim()) {
      toast.error("Assunto e corpo são obrigatórios");
      return;
    }
    setSending(true);
    setLastError(null);
    if (!isBatch) {
      // ── Single send ────────────────────────────────────────────────
      const to = seedLead.decision_maker_email || seedLead.email || seedLead.contact_email;
      if (!to) {
        toast.error("Lead sem email");
        setSending(false);
        return;
      }
      try {
        // Iter347 — Prefer /send-safe (queued+background). It always
        // returns 202 within ms so Cloudflare / gateway never has a
        // chance to 502 us. Falls back to /send-direct only if that
        // endpoint isn't reachable (older tenants, network partition).
        try {
          await axios.post(`${API}/smith/outreach/send-safe`,
            { to_email: to, subject, body, language }, { ...authHeader, timeout: 12000 });
        } catch (safeErr) {
          const safeStatus = safeErr?.response?.status;
          // Only fall through for gateway/routing issues (5xx / no-response);
          // an explicit 4xx (validation / blacklist / plan limit) is final.
          if (safeStatus && safeStatus < 500) throw safeErr;
          await axios.post(`${API}/smith/outreach/send-direct`,
            { to_email: to, subject, body }, { ...authHeader, timeout: 30000 });
        }
        toast.success(`Enviado para ${to}`);
        onSent?.();
        onClose();
      } catch (e) {
        const msg = e?.response?.data?.detail || e?.response?.statusText || e.message;
        setLastError({ to, message: msg, status: e?.response?.status });
        toast.error("Falha no envio via Resend",
          { description: `${msg} · Use o botão "Abrir no meu email" para enviar manualmente.` });
      } finally { setSending(false); }
      return;
    }
    // ── Batch send (sequential, 1 request per lead) ──────────────────
    const ok = [];
    const fail = [];
    const withEmail = leads.filter((L) => (L.decision_maker_email || L.email || L.contact_email));
    setProgress({ total: withEmail.length, done: 0, ok: 0, fail: 0 });
    for (const L of withEmail) {
      const to = L.decision_maker_email || L.email || L.contact_email;
      // Personalise subject/body: swap known tokens for the recipient's data.
      const personalisedSubject = subject
        .replaceAll("{name}", L.decision_maker_name || L.name || "")
        .replaceAll("{company}", L.company_name || L.property_name || L.company || "");
      const personalisedBody = body
        .replaceAll("{name}", L.decision_maker_name || L.name || "")
        .replaceAll("{company}", L.company_name || L.property_name || L.company || "")
        .replaceAll("{city}", L.city || "");
      try {
        // Iter347 — Same /send-safe first strategy for batch sends.
        try {
          await axios.post(`${API}/smith/outreach/send-safe`,
            { to_email: to, subject: personalisedSubject, body: personalisedBody, language },
            { ...authHeader, timeout: 12000 });
        } catch (safeErr) {
          const safeStatus = safeErr?.response?.status;
          if (safeStatus && safeStatus < 500) throw safeErr;
          await axios.post(`${API}/smith/outreach/send-direct`,
            { to_email: to, subject: personalisedSubject, body: personalisedBody },
            { ...authHeader, timeout: 30000 });
        }
        ok.push(to);
      } catch {
        fail.push(to);
      }
      setProgress({ total: withEmail.length, done: ok.length + fail.length,
                     ok: ok.length, fail: fail.length });
    }
    setSending(false);
    toast.success(`Batch concluído: ${ok.length} enviados, ${fail.length} falhas`);
    onSent?.({ ok, fail });
  };

  const doSaveSequence = async () => {
    if (!subject.trim() || !body.trim()) return;
    setSavingSeq(true);
    try {
      await axios.post(`${API}/smith/outreach/templates`,
        { name: `${(seedLead?.segment || "vs")} · ${subject.slice(0, 40)}`,
          subject, body,
          language, tone,
          tags: ["vehicle-security", "vs-drawer"] },
        { ...authHeader, timeout: 15000 });
      toast.success("Salvo como sequência");
    } catch (e) {
      toast.error("Falha ao salvar",
        { description: e?.response?.data?.detail || e.message });
    } finally { setSavingSeq(false); }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center"
         data-testid="claire-pitch-modal" onClick={onClose}>
      <div className="absolute inset-0 bg-black/70" />
      <div className="relative w-[720px] max-w-[96vw] max-h-[92vh] overflow-y-auto rounded-2xl border border-purple-500/30 p-5 space-y-3"
           style={{ background: "#0F172A" }}
           onClick={(e) => e.stopPropagation()}>
        <header className="flex items-start justify-between gap-2">
          <div>
            <h3 className="text-[15px] font-extrabold text-white inline-flex items-center gap-1.5">
              <Sparkles className="w-4 h-4 text-purple-300" />
              {isBatch ? `Batch Pitch (${leads.length})` : "Claire Pitch"}
            </h3>
            <p className="text-[11.5px] text-slate-400 mt-0.5">
              {isBatch
                ? `Preview + edição de mensagem para ${leads.length} leads · tokens {name} {company} {city} são substituídos por lead.`
                : `Para: ${seedLead?.decision_maker_email || seedLead?.email || "sem email"}`}
            </p>
          </div>
          <button type="button" onClick={onClose}
                  data-testid="claire-pitch-close"
                  className="w-8 h-8 rounded-lg border border-white/15 text-slate-400 hover:text-white flex items-center justify-center">
            <X className="w-4 h-4" />
          </button>
        </header>

        {isBatch && missingEmail > 0 && (
          <div className="text-[11.5px] text-amber-300 border border-amber-400/30 bg-amber-500/10 rounded-lg px-3 py-2 inline-flex items-center gap-2"
               data-testid="claire-pitch-missing-email">
            <AlertTriangle className="w-3.5 h-3.5" />
            {missingEmail} lead(s) sem email — serão ignorados no batch.
          </div>
        )}

        <div className="grid grid-cols-3 gap-2">
          <label className="text-[11px] text-slate-300 flex flex-col gap-1">
            Tom
            <select value={tone} onChange={(e) => setTone(e.target.value)}
                    data-testid="claire-pitch-tone"
                    className="h-8 px-2 rounded border border-white/15 bg-black/30 text-[12px] text-white">
              <option value="consultative">Consultivo</option>
              <option value="urgent">Urgente</option>
              <option value="empathetic">Empático</option>
              <option value="roi">ROI</option>
            </select>
          </label>
          <label className="text-[11px] text-slate-300 flex flex-col gap-1">
            Idioma
            <select value={language} onChange={(e) => setLanguage(e.target.value)}
                    data-testid="claire-pitch-lang"
                    className="h-8 px-2 rounded border border-white/15 bg-black/30 text-[12px] text-white">
              <option value="pt">Português</option>
              <option value="en">English</option>
              <option value="es">Español</option>
            </select>
          </label>
          <button type="button"
                  onClick={() => doGenerate(seedLead, tone, language)}
                  disabled={generating || !seedLead}
                  data-testid="claire-pitch-regen"
                  className="h-8 self-end px-3 rounded-lg border border-purple-500/40 bg-purple-500/15 text-purple-200 text-[11.5px] font-bold inline-flex items-center justify-center gap-1.5 disabled:opacity-50">
            {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        : <RefreshCw className="w-3.5 h-3.5" />}
            Regenerar
          </button>
        </div>

        <label className="text-[11px] text-slate-300 flex flex-col gap-1">
          Assunto
          <input value={subject} onChange={(e) => setSubject(e.target.value)}
                 data-testid="claire-pitch-subject"
                 disabled={generating}
                 className="h-9 px-3 rounded-lg border border-white/15 bg-black/30 text-[13px] text-white" />
        </label>
        <label className="text-[11px] text-slate-300 flex flex-col gap-1">
          Corpo <span className="text-slate-500 normal-case">(use {"{name}"}, {"{company}"}, {"{city}"} para personalização)</span>
          <textarea value={body} onChange={(e) => setBody(e.target.value)}
                    data-testid="claire-pitch-body"
                    disabled={generating}
                    rows={12}
                    className="rounded-lg border border-white/15 bg-black/30 p-3 text-[12.5px] text-white leading-relaxed" />
        </label>

        {progress && (
          <div className="text-[12px] text-slate-300 inline-flex items-center gap-3 border border-white/10 rounded-lg px-3 py-2"
               data-testid="claire-pitch-progress">
            <span>Enviando… {progress.done}/{progress.total}</span>
            <span className="text-emerald-300 inline-flex items-center gap-1">
              <CheckCircle2 className="w-3.5 h-3.5" /> {progress.ok}
            </span>
            <span className="text-rose-300 inline-flex items-center gap-1">
              <AlertTriangle className="w-3.5 h-3.5" /> {progress.fail}
            </span>
          </div>
        )}

        {/* Iter347 — Resend/backend bypass. If the primary send failed for
            any reason (Resend outage, Cloudflare 502, missing API key,
            unverified domain, tenant plan limits) we offer a one-click
            mailto fallback so the vendor NEVER hits a dead end. */}
        {lastError && !isBatch && (
          <div className="border border-amber-500/40 bg-amber-500/10 rounded-lg p-3 space-y-2"
               data-testid="claire-pitch-fallback">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-300 shrink-0 mt-0.5" />
              <div className="flex-1 text-[12px] text-amber-100">
                <div className="font-bold mb-0.5">
                  Resend indisponível{lastError.status ? ` (HTTP ${lastError.status})` : ""}
                </div>
                <div className="text-amber-200/80 text-[11.5px]">
                  {lastError.message?.slice(0, 220) || "Origem retornou resposta inválida."}
                </div>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <a
                href={`mailto:${encodeURIComponent(lastError.to)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`}
                data-testid="claire-pitch-mailto"
                onClick={() => {
                  // Assume user actually sends from their client → clean up.
                  setTimeout(() => { onSent?.(); onClose(); }, 800);
                }}
                className="h-8 px-3 rounded-lg text-[11.5px] font-bold text-white inline-flex items-center gap-1.5"
                style={{ background: "linear-gradient(135deg,#F59E0B 0%,#B45309 100%)" }}>
                <ExternalLink className="w-3 h-3" />
                Abrir no meu email
              </a>
              <button type="button"
                onClick={async () => {
                  await navigator.clipboard.writeText(`Para: ${lastError.to}\nAssunto: ${subject}\n\n${body}`);
                  toast.success("Copiado para o clipboard");
                }}
                data-testid="claire-pitch-copy"
                className="h-8 px-3 rounded-lg border border-amber-500/40 text-amber-200 text-[11.5px] font-bold inline-flex items-center gap-1.5 hover:bg-amber-500/10">
                <Save className="w-3 h-3" /> Copiar texto
              </button>
              <button type="button"
                onClick={doSend}
                data-testid="claire-pitch-retry"
                className="h-8 px-3 rounded-lg border border-amber-500/40 text-amber-200 text-[11.5px] font-bold inline-flex items-center gap-1.5 hover:bg-amber-500/10">
                <RefreshCw className="w-3 h-3" /> Tentar Resend novamente
              </button>
            </div>
          </div>
        )}

        <div className="flex flex-wrap gap-2 pt-2 border-t border-white/5">
          <button type="button" onClick={doSaveSequence} disabled={savingSeq || generating || !subject || !body}
                  data-testid="claire-pitch-save-seq"
                  className="h-9 px-3 rounded-lg border border-white/15 text-[12px] font-bold text-slate-200 hover:border-white/30 inline-flex items-center gap-1.5 disabled:opacity-50">
            {savingSeq ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            Salvar como sequência
          </button>
          <button type="button" onClick={() => document.querySelector('[data-testid="claire-pitch-body"]')?.focus()}
                  data-testid="claire-pitch-edit"
                  className="h-9 px-3 rounded-lg border border-white/15 text-[12px] font-bold text-slate-200 hover:border-white/30 inline-flex items-center gap-1.5">
            <Pencil className="w-3.5 h-3.5" /> Editar
          </button>
          <button type="button" onClick={doSend}
                  disabled={sending || generating || !subject || !body}
                  data-testid="claire-pitch-send"
                  className="ml-auto h-9 px-4 rounded-lg text-[12px] font-bold text-white inline-flex items-center gap-1.5 disabled:opacity-50"
                  style={{ background: "linear-gradient(135deg,#7C3AED 0%,#4C1D95 100%)" }}>
            {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            {isBatch ? `Enviar em lote (${leads.filter((L) => (L.decision_maker_email || L.email)).length})` : "Enviar via Resend"}
          </button>
        </div>
      </div>
    </div>
  );
}
